import { APIEndpoints } from "../constants/endpoints.ts";
import { RequestParams } from "../types/global.ts";
import { TUserProfilePayload, TUserSelfPayload } from "../types/users.ts";
import { Request } from "./utils/network.ts";

/**
 * Get all users with pagination options
 * @param params Request parameters including organization_id, pageNumber, pageSize, etc.
 */
export const getAllUsers = (params: RequestParams) => {
  return Request({
    path: APIEndpoints.USERS.BASE,
    type: "GET",
    params,
  });
};

/**
 * Get current user information
 * @param params Request parameters
 */
export const getSelf = (params: RequestParams = {}) => {
  return Request({
    path: APIEndpoints.USERS.SELF,
    type: "GET",
    params,
  });
};

/**
 * Update current user information
 * @param params Request parameters including user data to update
 * @param payload The user data to update
 */
export const updateSelf = (
  params: RequestParams,
  payload: TUserSelfPayload
) => {
  return Request({
    path: APIEndpoints.USERS.SELF,
    type: "PATCH",
    params,
    body: payload,
  });
};

/**
 * Delete current user account
 * @param params Request parameters (e.g., organization_id)
 */
export const deleteSelf = (params: RequestParams) => {
  return Request({
    path: APIEndpoints.USERS.SELF,
    type: "DELETE",
    params,
  });
};

/**
 * Get a user by ID
 * @param userId The ID of the user to retrieve
 * @param params Request parameters including organization_id
 */
export const getUserById = (userId: string, params: RequestParams) => {
  return Request({
    path: APIEndpoints.USERS.BY_ID(userId),
    type: "GET",
    params,
  });
};

/**
 * Delete a user by ID
 * @param userId The ID of the user to delete
 * @param params Request parameters including organization_id
 */
export const deleteUser = (userId: string, params: RequestParams) => {
  return Request({
    path: APIEndpoints.USERS.BY_ID(userId),
    type: "DELETE",
    params,
  });
};

/**
 * Update a user's profile
 * @param userId The ID of the user whose profile to update
 * @param params Request parameters including organization_id and profile data
 * @param payload The profile data to update
 */
export const updateUserProfile = (
  userId: string,
  params: RequestParams,
  payload: TUserProfilePayload
) => {
  return Request({
    path: APIEndpoints.USERS.PROFILE(userId),
    type: "PATCH",
    params,
    body: payload,
  });
};

/**
 * Get current user invitations
 * @param params Request parameters
 */
export const getSelfInvitations = (params: RequestParams = {}) => {
  return Request({
    path: APIEndpoints.USERS.INVITATIONS,
    type: "GET",
    params,
  });
};

export const acceptSelfInvitation = (
  id: string,
  params: RequestParams = {}
) => {
  return Request({
    path: `${APIEndpoints.USERS.INVITATIONS}/${id}`,
    type: "POST",
    params,
  });
};

export const rejectSelfInvitation = (
  id: string,
  params: RequestParams = {}
) => {
  return Request({
    path: `${APIEndpoints.USERS.INVITATIONS}/${id}`,
    type: "DELETE",
    params,
  });
};
